// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import { gql, useLazyQuery } from "@apollo/client";
import { Card, Fade, Icon, Menu, MenuItem, Modal, Tooltip } from "@mui/material";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import DataTable from "components/DataTable";
import CustomPagination from "components/Pagination";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { getUproColor } from "utils/colors";
import dataConvenios from "../data/dataConvenios";
import ModalAdd from "./ModalAdd";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "70%", xxl: "60%" },
  overflowY: "auto",
};

const GET_CONVENIOS = gql`
  query convenios($page: Int, $order: Order, $filter: JSON) {
    paginationInfo {
      pages
      total
    }
    convenios(pagination: { page: $page, limit: 10 }, order: $order, filter: $filter) {
      id
      nombreInstitucion
    }
  }
`;

export default function ModalConvenio({ open, handleClose }) {
  const [convenio, setConvenio] = useState();
  const [rows, setRows] = useState([]);
  const [openMenu, setOpenMenu] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState();
  const { columns, getRows } = dataConvenios;
  const { handleSnackbar } = useContext(MessageManager);
  const [search, setSearch] = useState("");
  const [searched, setSearched] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "nombreInstitucion",
    orden: "ASC",
  });
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setPage(1);
      setSearch(event.target.value);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const [getConvenios, { loading, data }] = useLazyQuery(GET_CONVENIOS, {
    variables: {
      search: search || null,
      page: page,
      order: { field: orderBy.campo, order: orderBy.orden },
    },
    fetchPolicy: "no-cache",
    cachePolicy: "no-cache",
  });

  const handleOpenMenu = (event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedItem(item);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenMenu(false);
  };

  const renderMenu = (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem
        onClick={() => {
          setOpenModalEdit(true);
          handleCloseMenu();
          setConvenio(selectedItem);
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Editar</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getUproColor("uproBlue"),
              }}
            >
              edit
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
      <MenuItem
        onClick={() => {
          setOpenModalDelete(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Eliminar</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getUproColor("uproPink"),
              }}
            >
              delete
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
    </Menu>
  );

  useEffect(() => {
    setConvenio();
    getConvenios();
    setSearch("");
  }, [open]);

  useEffect(() => {
    if (data && data.convenios) {
      setRows(getRows(data.convenios, handleOpenMenu));
      setPagesInfo(data.paginationInfo);
    }
  }, [data]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" p={3}>
              <SoftBox>
                <SoftTypography variant="h6">Convenios</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="flex-end">
                <SoftBox mr={2}>
                  <SoftInput
                    placeholder="Escribe aquí..."
                    icon={{ component: "search", direction: "left" }}
                    onChange={handleSearch}
                  />
                </SoftBox>
                <SoftBox mr={2}>
                  <Tooltip title="Agregar nuevo convenio" placement="top">
                    <SoftButton color="primary" circular onClick={() => setOpenModalEdit(true)}>
                      <Icon>add</Icon>
                      &nbsp;Agregar
                    </SoftButton>
                  </Tooltip>
                </SoftBox>
                <SoftBox display="flex" alignItems="center">
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox
              sx={{
                maxHeight: "50vh",
                overflow: "auto",
              }}
            >
              {loading ? (
                <Loading />
              ) : data && data.convenios && data.convenios.length > 0 ? (
                <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            <CustomPagination
              loading={loading}
              length={rows?.length}
              total={pagesInfo?.total}
              pages={pagesInfo?.pages}
              page={page}
              setPage={setPage}
              noCard
            />
            {renderMenu}
          </SoftBox>
          <ModalAdd
            open={openModalEdit}
            handleClose={() => {
              setOpenModalEdit(false);
            }}
            data={convenio}
            refetch={() => getConvenios()}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalConvenio.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};
